import React from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  BookOpen, FilmStrip, Heart, MagnifyingGlass, SignIn, SignOut, List, X,
  Microphone, MusicNotes, Users, HandsPraying, BookBookmark, Scroll, Hash, Gear, UserCircle, ChatCircleDots, Church, Sparkle, Clock, NotePencil, VideoCamera, Phone, BellSimpleRinging, Stethoscope, Queue, Fire, SmileyWink, Moon, Sun, ChartLineUp, CaretLeft, CaretRight, ForkKnife, Gift, PlayCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "./UserAvatar";
import { useAudio } from "../context/AudioContext";
import { useTheme } from "../context/ThemeContext";
import { GoLiveButton, LiveHeaderBadge } from "./Live";
import BirthdayHeaderBadge from "./BirthdayHeaderBadge";
import PrayerBeaconButton from "./PrayerBeaconButton";
import useInboxCount from "../hooks/useInboxCount";
import ThemePickerButton from "./ThemePickerButton";
import DarkModeToggle from "./DarkModeToggle";
import CCToggleButton from "./CCToggleButton";
import BrandMark from "./BrandMark";
import LanguagePicker from "./LanguagePicker";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { useTranslation } from "react-i18next";

const primaryNav = [
  { to: "/bible", labelKey: "nav.bible", label: "Bible", icon: BookOpen },
  { to: "/rooms", labelKey: "nav.rooms", label: "Rooms", icon: Hash },
  { to: "/prayer-wall", labelKey: "nav.prayers", label: "Prayers", icon: HandsPraying },
  { to: "/sermons", labelKey: "nav.sermons", label: "Sermons", icon: Microphone },
  { to: "/movies", labelKey: "nav.movies", label: "Movies", icon: FilmStrip },
  { to: "/kids", labelKey: "nav.kids", label: "Kids", icon: BookBookmark },
  { to: "/churches", labelKey: "nav.churches", label: "Churches", icon: Church },
];

// More menu — alphabetical (Feb 2026). Every section that lives in
// the scrolling top rail is also reachable here, predictably sorted
// by label so the user doesn't have to scan.
const moreNav = [
  { to: "/videos",                label: "Animated Bible Stories",            icon: FilmStrip },
  { to: "/apocrypha",             label: "Apocrypha",                         icon: Scroll },
  { to: "/precepts",              label: "Bible Precepts",                    icon: Scroll },
  { to: "/bookmarks",             label: "Bookmarks",                         icon: Heart },
  { to: "/christian-rap",         label: "Faith-Based Rap",                   icon: Microphone },
  { to: "/choir",                 label: "Choir",                             icon: Users },
  { to: "/comedy",                label: "Comedy",                            icon: SmileyWink },
  { to: "/concordance",           label: "Concordance",                       icon: BookOpen },
  { to: "/congregation",          label: "Congregation Live",                 icon: VideoCamera },
  { to: "/studio",                label: "Creator Studio",                    icon: ChartLineUp },
  { to: "/diagnostics",           label: "Device Check",                      icon: Stethoscope },
  { to: "/dictionary",            label: "Dictionary",                        icon: BookOpen },
  { to: "/discover",              label: "Discover (Trending)",               icon: Fire },
  { to: "/movies/black-american", label: "Foundational Black American Films", icon: FilmStrip },
  { to: "/gospel-music",          label: "Gospel Music",                      icon: MusicNotes },
  // (Removed duplicate "Kids & Cartoons" entry — already shown as
  // "Kids" in the primary top-nav above. Keeping both meant the
  // drawer showed two rows that opened the exact same page.)
  { to: "/messages",              label: "Messages",                          icon: ChatCircleDots },
  { to: "/music-videos",          label: "Music Videos",                      icon: FilmStrip },
  { to: "/my-lineup",             label: "My Lineup",                         icon: Queue },
  { to: "/my-notes",              label: "My Notes",                          icon: NotePencil },
  { to: "/my-prayers",            label: "My Prayers",                        icon: HandsPraying },
  { to: "/profile",               label: "My Profile",                        icon: UserCircle },
  { to: "/storybook",             label: "Picture Bible (AI)",                icon: BookOpen },
  { to: "/playlists",             label: "Playlists",                         icon: PlayCircle },
  { to: "/circles",               label: "Prayer Circles",                    icon: HandsPraying },
  { to: "/memorial",              label: "Resting in Peace Wall",             icon: Heart },
  { to: "/search",                label: "Search",                            icon: MagnifyingGlass },
  { to: "/study-group",           label: "Study Group (Wednesdays)",          icon: Users },
  { to: "/sunday-school",         label: "Sunday School (kids)",              icon: BookBookmark },
  { to: "/recipes",               label: "Sunday Dinner Recipes",             icon: ForkKnife },
  { to: "/support",               label: "Support us",                        icon: Heart },
  { to: "/wall",                  label: "Wall of Faith",                     icon: Heart },
  { to: "/invite",                label: "Invite Friends · Earn Premium",     icon: Gift },
  { to: "/lessons",               label: "Wednesday Lessons",                 icon: BookBookmark },
];

export default function Header() {
  const { user, logout } = useAuth();
  const inbox = useInboxCount();
  const { language, setLanguage } = useAudio();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  // Drawer ref + auto-focus effect — when the hamburger drawer opens,
  // immediately focus the first interactive row so keyboard users can
  // press ArrowDown to navigate without first hitting Tab. Added
  // Feb 2026 per owner request: "let me open the menu and use the
  // arrows to go up/down without clicking anything".
  const drawerRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    // Wait one frame so the drawer DOM mounts before we look it up.
    // NOTE: must skip HIDDEN elements (e.g. the md:hidden AI/Voice
    // buttons at the top of the drawer on laptop) — focusing a
    // display:none node silently fails and arrow keys then scroll
    // the page instead of walking the menu titles.
    const id = requestAnimationFrame(() => {
      const first = Array.from(
        drawerRef.current?.querySelectorAll('a[href], button:not([disabled])') || []
      ).find((el) => el.offsetParent !== null);
      first?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);
  const location = useLocation();

  // Horizontally-scrollable top nav. Mirrors the CategoryChipRail
  // bottom toolbar so the user can swipe through the primary sections
  // (Bible / Rooms / Prayers / Sermons / Movies / Kids / Churches /
  // More) the same way they swipe the chip rail below — drag-to-pan,
  // hidden scrollbar, fade edges, ◀ / ▶ chevrons on desktop. User
  // request 2026-02: "make the top bar scroll like the bottom toolbar".
  const navRailRef = React.useRef(null);
  const [navShowLeft, setNavShowLeft] = React.useState(false);
  const [navShowRight, setNavShowRight] = React.useState(false);
  const updateNavChevrons = React.useCallback(() => {
    const rail = navRailRef.current;
    if (!rail) return;
    setNavShowLeft(rail.scrollLeft > 6);
    setNavShowRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 6);
  }, []);
  React.useEffect(() => {
    updateNavChevrons();
    const rail = navRailRef.current;
    if (!rail) return;
    rail.addEventListener("scroll", updateNavChevrons, { passive: true });
    window.addEventListener("resize", updateNavChevrons);
    // Re-measure when the rail's own size changes (font load, icon
    // SVGs decoding, the More dropdown opening) so the chevrons
    // appear/disappear in lockstep with actual overflow rather than
    // sticking on a stale mount-time snapshot.
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateNavChevrons);
      ro.observe(rail);
    }
    // One delayed pass for environments where ResizeObserver doesn't
    // fire on initial paint (older WebKit on tablets).
    const t = setTimeout(updateNavChevrons, 400);
    return () => {
      rail.removeEventListener("scroll", updateNavChevrons);
      window.removeEventListener("resize", updateNavChevrons);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, [updateNavChevrons]);
  const scrollNavBy = (dir) => {
    const rail = navRailRef.current;
    if (!rail) return;
    const delta = Math.round(rail.clientWidth * 0.8) * (dir === "left" ? -1 : 1);
    rail.scrollBy({ left: delta, behavior: "smooth" });
  };
  // Pointer drag-to-pan — desktop trackpad / mouse users. We do NOT
  // call setPointerCapture so child NavLink clicks still fire when
  // the user simply taps (tap = no drag distance traveled).
  const navDragRef = React.useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });
  const onNavPointerDown = (e) => {
    const rail = navRailRef.current;
    if (!rail) return;
    navDragRef.current = { active: true, startX: e.clientX, startScroll: rail.scrollLeft, moved: 0 };
  };
  const onNavPointerMove = (e) => {
    const st = navDragRef.current;
    if (!st.active) return;
    const rail = navRailRef.current;
    if (!rail) return;
    const dx = e.clientX - st.startX;
    if (Math.abs(dx) > 4) {
      st.moved += Math.abs(dx);
      rail.scrollLeft = st.startScroll - dx;
    }
  };
  const onNavPointerUp = (e) => {
    const st = navDragRef.current;
    const wasDragging = st.active && st.moved > 6;
    navDragRef.current = { active: false, startX: 0, startScroll: 0, moved: 0 };
    if (wasDragging) {
      // Real drag — swallow the click so a drag-start over Bible
      // doesn't navigate to /bible on release.
      e.preventDefault();
      e.stopPropagation();
    }
  };

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  // Whether the signed-in user has explicitly opted in to having a
  // channel. We only show the "My Channel" menu item when this is
  // true — regular users (premium or not) who haven't activated stay
  // out of the public channel surface. Listens for the activate /
  // deactivate bus events so the link appears/disappears immediately
  // when the user flips the toggle.
  const [myChannelActive, setMyChannelActive] = React.useState(false);
  React.useEffect(() => {
    if (!user?.id) { setMyChannelActive(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { getChannel } = await import("../lib/channels");
        const ch = await getChannel(user.id);
        if (!cancelled) setMyChannelActive(!!ch?.is_channel_active);
      } catch (_) { /* anon / 404 / network — keep hidden */ }
    })();
    const onAct  = () => setMyChannelActive(true);
    const onDeact = () => setMyChannelActive(false);
    window.addEventListener("hw:channel:activated",   onAct);
    window.addEventListener("hw:channel:deactivated", onDeact);
    return () => {
      cancelled = true;
      window.removeEventListener("hw:channel:activated",   onAct);
      window.removeEventListener("hw:channel:deactivated", onDeact);
    };
  }, [user?.id]);

  return (
    <header
      className="sticky top-0 z-50 glass border-b border-cream-300/60"
      data-testid="app-header"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-7xl mx-auto px-3 md:px-8 py-3 md:py-3.5 flex items-center justify-between gap-2 md:gap-3">
        <Link to="/" className="flex items-center gap-2 md:gap-2.5 group shrink-0 min-w-0" data-testid="brand-link">
          <BrandMark size={36} className="transition-transform group-hover:scale-105 shrink-0" />
          {/* Wordmark — smaller on mobile so it fits next to the
              brand mark without crowding the language picker /
              menu / sign-in pill. Tagline still hidden under md to
              keep the line short. */}
          <div className="flex flex-col leading-none min-w-0">
            <span className="font-heading text-3xl sm:text-3xl md:text-4xl font-semibold text-forest truncate">Holy Words</span>
            {/* Tagline removed on desktop per owner request (Feb 2026) —
                keep the header lean and mobile-like at every breakpoint. */}
          </div>
        </Link>

        {/* Desktop nav rail removed (Feb 2026) per owner: "make the top
            rail look just like the mobile phone app — everything in the
            swipe chip rail, drop-down menu, and shortcut menu". The
            chip rail directly below the header handles category nav
            now; the hamburger drawer holds full alphabetical nav. */}
        <div className="hidden flex-1" data-testid="header-nav-rail-removed">
          {/* Left fade + chevron — only when there's more rail off-screen */}
          {navShowLeft && (
            <>
              <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-cream/95 to-transparent z-10" />
              <button
                type="button"
                onClick={() => scrollNavBy("left")}
                data-testid="header-nav-prev"
                aria-label="Scroll nav left"
                className="hidden lg:grid absolute left-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-cream-200 hover:bg-cream-300 text-ink place-items-center shadow-md z-20"
              >
                <CaretLeft size={11} weight="bold" />
              </button>
            </>
          )}

          <nav
            ref={navRailRef}
            data-testid="header-nav-rail"
            onPointerDown={onNavPointerDown}
            onPointerMove={onNavPointerMove}
            onPointerUp={onNavPointerUp}
            onPointerCancel={onNavPointerUp}
            style={{ touchAction: "pan-x", overscrollBehaviorX: "contain" }}
            className="flex items-center gap-1 overflow-x-auto scroll-smooth cursor-grab active:cursor-grabbing select-none w-full [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {primaryNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                data-nav-to={n.to}
                className={({ isActive }) =>
                  `shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                    isActive ? "bg-forest text-cream shadow-sm" : "text-ink/80 hover:text-forest hover:bg-cream-200"
                  }`
                }
              >
                <n.icon size={15} weight="bold" />
                {n.labelKey ? t(n.labelKey) : n.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="more-nav-trigger"
                className="shrink-0 flex items-center gap-1 px-3.5 py-2 rounded-full text-sm whitespace-nowrap text-ink/80 hover:text-forest hover:bg-cream-200 transition-all outline-none"
              >
                {t("nav.more")} <span className="text-xs">▾</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-2xl bg-cream-100/95 backdrop-blur-md border-cream-300 mt-2 min-w-[220px]">
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link to="/demo" data-testid="more-nav-demo" className="flex items-center gap-2 text-sm cursor-pointer font-semibold bg-gradient-to-r from-amber-100 to-amber-50 rounded-lg">
                      <Sparkle size={15} weight="fill" className="text-amber-600" />
                      Demo Mode
                      <span className="ml-auto text-[9px] tracking-[0.2em] uppercase text-amber-700 font-bold bg-amber-200 px-1.5 py-0.5 rounded-full">Owner</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                {/* "My Channel" menu item removed per owner request —
                    the linked /u/{user_id} channel surface isn't ready
                    yet. We keep the activate/deactivate API surface
                    intact (used elsewhere) so re-enabling later is a
                    one-line change. */}
                {moreNav.map((n) => (
                  <DropdownMenuItem key={n.to} asChild>
                    <Link to={n.to} data-testid={`more-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <n.icon size={15} weight="bold" className="text-terracotta" />
                      {n.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right fade + chevron */}
          {navShowRight && (
            <>
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-cream/95 to-transparent z-10" />
              <button
                type="button"
                onClick={() => scrollNavBy("right")}
                data-testid="header-nav-next"
                aria-label="Scroll nav right"
                className="hidden lg:grid absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-cream-200 hover:bg-cream-300 text-ink place-items-center shadow-md z-20"
              >
                <CaretRight size={11} weight="bold" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Red LIVE pill — shows on every page while someone is
              broadcasting publicly. Tap to jump into the stream. */}
          <LiveHeaderBadge />
          <LanguagePicker value={language} onChange={setLanguage} />

          {user && user !== false ? (
            <div className="hidden md:flex items-center gap-2">
              {/* Desktop header simplified Feb 2026 — owner asked for
                  the laptop header to mirror the mobile app. Only:
                  Holy Words logo + LanguagePicker + DarkModeToggle +
                  Logout/Sign-in + hamburger (more menu). Settings,
                  CC, ThemePicker, PrayerBeacon, Birthday, GoLive,
                  avatar — all moved into the hamburger drawer where
                  they're already accessible via mobile-style nav. */}
              <DarkModeToggle />
              <button
                onClick={logout}
                data-testid="logout-button"
                className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-cream-200 hover:bg-cream-300 text-xs transition-all"
              >
                <SignOut size={14} weight="bold" /> Logout
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <DarkModeToggle />
              <Link
                to="/login"
                data-testid="login-link"
                className="flex items-center gap-1.5 px-4 h-9 rounded-full bg-forest hover:bg-forest-600 text-cream text-xs transition-all"
              >
                <SignIn size={14} weight="bold" /> {t("nav.signIn")}
              </Link>
            </div>
          )}

          {/* Inline AI + Voice — replaced the floating sparkle/mic FABs
              (owner: "they're getting in the way"). Present on every page. */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-ai"))}
            data-testid="header-ai-button"
            aria-label="Ask Holy Words AI"
            title="Ask AI"
            className="hidden md:grid w-9 h-9 place-items-center rounded-full bg-cream-200 hover:bg-cream-300 transition-colors"
          >
            <Sparkle size={17} weight="fill" className="text-[#E7C47C]" />
          </button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-voice"))}
            data-testid="header-voice-button"
            aria-label="Voice commands"
            title="Voice commands"
            className="hidden md:grid w-9 h-9 place-items-center rounded-full bg-cream-200 hover:bg-cream-300 transition-colors"
          >
            <Microphone size={17} weight="fill" className="text-terracotta" />
          </button>

          {/* Hamburger / more-menu button — shown at ALL breakpoints
              now (desktop + mobile) so the laptop header matches the
              phone layout. Opens the same alphabetical drawer that
              lists every page, Go Live, Settings, and theme picker. */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-button"
            className="relative w-9 h-9 grid place-items-center rounded-full bg-cream-200"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <List size={18} />}
            {!open && inbox.total > 0 && (
              <span data-testid="menu-inbox-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-cream text-[10px] font-bold grid place-items-center">
                {inbox.total > 99 ? "99+" : inbox.total}
              </span>
            )}
          </button>

          {/* Mobile dark-mode quick-toggle. Lives in the toolbar so
              phone users can flip light/dark without opening the
              menu, matching what desktop has. md+ already has the
              full DarkModeToggle inside the user-section above, so
              we only render this below md to avoid duplication. */}
          <div className="md:hidden">
            <DarkModeToggle />
          </div>

          {/* Last-item in the toolbar — Sign in (when logged out) or
              Sign out (when logged in). Mobile-only since md+ already
              has the labeled Sign In / Logout pill earlier in the bar. */}
          {user && user !== false ? (
            <button
              type="button"
              onClick={logout}
              data-testid="top-logout-button"
              aria-label="Logout"
              title="Logout"
              className="md:hidden inline-flex items-center gap-1 px-3 h-9 rounded-full bg-terracotta hover:bg-terracotta-600 text-cream text-[10px] font-bold tracking-[0.18em] uppercase shadow-md transition-all"
            >
              <SignOut size={13} weight="bold" />
              <span>{t("nav.signOut")}</span>
            </button>
          ) : (
            <Link
              to="/login"
              data-testid="top-signin-button"
              aria-label="Sign in"
              title="Sign in"
              className="md:hidden inline-flex items-center gap-1 px-3 h-9 rounded-full bg-forest hover:bg-forest-600 text-cream text-[10px] font-bold tracking-[0.18em] uppercase shadow-md transition-all"
            >
              <SignIn size={13} weight="bold" />
              <span>{t("nav.signIn")}</span>
            </Link>
          )}
        </div>
      </div>

      {open && createPortal((
        <>
          {/* Portaled to document.body — the parent <header> has
              `glass` (backdrop-filter:blur) which creates a CSS
              containing block, and `position:fixed` children inside
              a containing block are constrained to the ancestor's
              box instead of the viewport. That's why the drawer was
              clipping to ~120 px (header height) and only showing
              the first 2 alphabetical items. Portaling escapes the
              header entirely so the drawer covers the real viewport.
              Same trick we use for the movie player modal. */}
          <div
            className="fixed inset-0 top-[64px] z-[100] bg-ink/90 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-backdrop"
            aria-hidden="true"
          />
          {/* Backdrop above clips at all breakpoints now (Feb 2026 fix:
              previously had `md:hidden` so the drawer never showed on
              desktop after the header simplification). */}
          <div
            className="fixed left-0 right-0 top-[64px] z-[101] border-t border-cream-300 bg-cream px-5 py-3 flex flex-col gap-1 overflow-y-auto overflow-x-hidden md:left-auto md:right-4 md:max-w-md md:rounded-2xl md:border md:shadow-2xl md:top-[72px]"
            style={{
              // Drawer takes the full screen height MINUS the header
              // (~64 px) so it's a true app-style nav sheet. Bottom
              // padding clears the BottomNav bar + iOS safe area.
              bottom: 0,
              paddingBottom: "calc(env(safe-area-inset-bottom) + 6rem)",
              // Lock the drawer to vertical scroll only. Without
              // `touch-action: pan-y` a diagonal finger drag inside
              // the drawer triggered Chrome's swipe-back gesture or
              // visually slid the panel sideways — the user
              // reported this as "the menu wobbles side to side".
              // `pan-y` says: scrolling here is one-dimensional.
              touchAction: "pan-y",
              overscrollBehaviorX: "none",
            }}
            data-testid="mobile-nav-drawer"
            ref={drawerRef}
            // Keyboard nav (Feb 2026 owner request) — ArrowDown / Up
            // moves visual focus row-by-row through every drawer
            // item, Enter activates, Esc closes. We use a roving-
            // tabindex pattern: only the currently-focused item is
            // tab-reachable, so screen readers + sighted keyboard
            // users get the same model. The handler queries the DOM
            // each press so it survives re-renders without needing
            // to track a refs map. Home/End jump to first/last.
            onKeyDown={(e) => {
              const focusable = drawerRef.current
                ? Array.from(drawerRef.current.querySelectorAll('a[href], button:not([disabled])'))
                  .filter((el) => el.offsetParent !== null)
                : [];
              if (focusable.length === 0) return;
              const idx = focusable.indexOf(document.activeElement);
              let next = idx;
              switch (e.key) {
                case "ArrowDown": next = idx < 0 ? 0 : (idx + 1) % focusable.length; e.preventDefault(); break;
                case "ArrowUp":   next = idx <= 0 ? focusable.length - 1 : idx - 1; e.preventDefault(); break;
                case "Home":      next = 0; e.preventDefault(); break;
                case "End":       next = focusable.length - 1; e.preventDefault(); break;
                case "Escape":    setOpen(false); e.preventDefault(); return;
                default: return;
              }
              focusable[next]?.focus({ preventScroll: false });
            }}
          >
          {/* Mobile quick actions — AI + Voice moved OUT of the top bar
              (they crowded the Sign in/out button) into the drawer. */}
          <div className="flex gap-2 mb-2 md:hidden">
            <button
              onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("holywords:open-ai")); }}
              data-testid="drawer-ai-button"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-forest text-cream text-xs font-bold"
            >
              <Sparkle size={14} weight="fill" className="text-[#E7C47C]" /> Ask AI
            </button>
            <button
              onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("holywords:open-voice")); }}
              data-testid="drawer-voice-button"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-terracotta text-cream text-xs font-bold"
            >
              <Microphone size={14} weight="fill" /> Voice
            </button>
          </div>
          {/* Combined nav for the mobile drawer — all primary + more
              entries (and "My Channel" when active) merged into a
              single alphabetical list. User explicitly asked for
              alphabetical order so the menu reads like an index, not
              a feature-priority list. We dedupe by label so things
              like "/feed" appearing twice in primaryNav + moreNav
              don't show up as duplicate rows. */}
          {(() => {
            const all = [
              ...primaryNav,
              // "My Channel" mobile menu entry removed per owner
              // request — the channel surface isn't ready. Activate
              // API stays in case we re-enable later.
              ...moreNav,
            ];
            const seen = new Set();
            const deduped = all.filter((item) => {
              const key = (item.label || "").toLowerCase();
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            return deduped.sort((a, b) => a.label.localeCompare(b.label));
          })().map((n) => (
            <NavLink
              key={n.to + ":" + n.label}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm ${
                  isActive ? "bg-forest text-cream" : "text-ink hover:bg-cream-200"
                }`
              }
            >
              <n.icon size={16} weight="bold" />
              {n.labelKey ? t(n.labelKey) : n.label}
              {n.to === "/messages" && inbox.total > 0 && (
                <span data-testid="nav-messages-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-terracotta text-cream text-[11px] font-bold grid place-items-center">
                  {inbox.total > 99 ? "99+" : inbox.total}
                </span>
              )}
            </NavLink>
          ))}
          {user && user !== false ? (
            <>
              {/* Go Live — was previously desktop-only (hidden inside
                  a `md:flex` row). Now exposed in the mobile drawer
                  too so users on phones can start a broadcast. */}
              <GoLiveButton
                className="!w-full !h-auto !px-4 !py-3 !rounded-xl !justify-start !text-sm"
              />
              {/* Mobile menu dark-mode row — labelled version of the
                  toolbar toggle, so users who don't recognize the
                  sun/moon icon can still find it from the menu. */}
              <MobileDarkRow />
              <Link to="/settings" data-testid="mobile-settings-link" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-ink hover:bg-cream-200">
                <Gear size={16} /> Settings
              </Link>
              <button onClick={logout} data-testid="mobile-logout-button" className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-cream-200 mt-2">
                <SignOut size={16} /> Logout
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="mobile-login-link" className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-forest text-cream mt-2">
              <SignIn size={16} /> {t("nav.signIn")}
            </Link>
          )}
        </div>
        </>
      ), document.body)}
    </header>
  );
}


// MobileDarkRow — labelled "Dark mode" row inside the mobile drawer
// menu. Cycles light → midnight → oled → light to mirror the toolbar
// toggle.
function MobileDarkRow() {
  const { themeId, setThemeId } = useTheme();
  const ring = ["sanctuary", "midnight", "oled"];
  const idx = ring.indexOf(themeId);
  const next = idx === -1 ? "midnight" : ring[(idx + 1) % ring.length];
  const label = themeId === "oled"     ? "Pure black mode (OLED)"
             : themeId === "midnight" ? "Dark mode (Navy)"
             :                          "Light mode";
  const stateBadge = themeId === "oled"     ? "PURE BLACK"
                  : themeId === "midnight" ? "DARK"
                  :                          "LIGHT";
  const stateColor = themeId === "oled"     ? "text-white"
                  : themeId === "midnight" ? "text-amber-600"
                  :                          "text-muted_ink";
  const IconEl = themeId === "oled" ? Sun : (themeId === "midnight" ? Sun : Moon);
  return (
    <button
      type="button"
      onClick={() => setThemeId(next)}
      data-testid="mobile-dark-mode-row"
      className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm text-ink hover:bg-cream-200 w-full"
    >
      <span className="flex items-center gap-2">
        <IconEl size={16} weight={themeId === "sanctuary" ? "duotone" : "fill"} />
        {label}
      </span>
      <span className={`text-[10px] tracking-[0.18em] uppercase font-bold ${stateColor}`}>
        {stateBadge}
      </span>
    </button>
  );
}
